// IssueApiButton.tsx
import {
  ActionIcon,
  Alert,
  Button,
  Code,
  CopyButton,
  Divider,
  Group,
  Menu,
  Modal,
  Stack,
  Switch,
  Text,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconDotsVertical } from "@tabler/icons-react";
import React, { useMemo, useState } from "react";
import PowerBICollapsible from "../../../../components/PowerBICollapsible";
import { selectCurrentProjectId } from "../../../../global/globalSliceCommon";
import { useAppSelector } from "../../../../hooks";
import UserSettingsModalContent from "../../../user/UserSettingsModalContent";
import { selectIssueBoardId } from "../redux/selectors/issueCommonSelectors";

const CopyBtn: React.FC<{ value: string }> = ({ value }) => (
  <CopyButton value={value} timeout={1200}>
    {({ copied, copy }) => (
      <Tooltip label={copied ? "Kopiert!" : "Kopier"} withinPortal>
        <Button
          variant="secondary"
          size="sm"
          onClick={copy}
          leftSection={<i className="fa fa-copy fa-fw" />}
          style={{ minWidth: 110 }}
        >
          {copied ? "Kopiert" : "Kopier"}
        </Button>
      </Tooltip>
    )}
  </CopyButton>
);

const Row: React.FC<{ label: string; value: string; block?: boolean }> = ({ label, value, block }) => (
  <Stack gap={4}>
    {label && (
      <Text size="sm" c="dimmed">
        {label}
      </Text>
    )}
    <Group justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
      <Code
        block={block}
        style={{
          overflowX: "auto",
          flex: 1,
          whiteSpace: block ? "pre" : "nowrap",
          maxHeight: "120px",
          fontSize: "0.75rem",
        }}
      >
        {value}
      </Code>
      <CopyBtn value={value} />
    </Group>
  </Stack>
);

const IssueApiModal: React.FC<{ opened: boolean; onClose: () => void }> = ({ opened, onClose }) => {
  const projectId = useAppSelector(selectCurrentProjectId);
  const issueboardId = useAppSelector(selectIssueBoardId);
  const [includeTasks, setIncludeTasks] = useState(false);

  const url = useMemo(() => {
    if (!projectId || !issueboardId) return "";
    const baseUrl = `${window.location.origin}/api/trpc/project/${projectId}/issues/external`;
    const params = new URLSearchParams({
      issueboardId: issueboardId!,
      apiKey: "DIN_API_KEY",
    });
    if (includeTasks) {
      params.append("includeTasks", "true");
    }
    return `${baseUrl}?${params.toString()}`;
  }, [projectId, issueboardId, includeTasks]);

  const openUserSettings = () => {
    modals.open({
      title: "Brukerinnstillinger",
      centered: true,
      size: "40vw",
      radius: "lg",
      children: <UserSettingsModalContent />,
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="API-tilgang for dette registret"
      centered
      radius="lg"
      size="40vw"
      withinPortal
    >
      <Stack gap="lg">
        <Alert variant="light" color="blue" icon={<i className="fa fa-info-circle" />}>
          <Stack gap={8}>
            <Text size="sm" fw={600}>
              📋 KOPIER URLEN UNDER
            </Text>
            <Text size="sm">
              <b>TRINN 1:</b> Erstatt <Code>DIN_API_KEY</Code> med din personlige API-nøkkel
            </Text>
            <Text size="sm">
              <b>TRINN 2:</b> Lim inn i Power BI, Postman eller annet verktøy
            </Text>
            <Text size="sm" c="dimmed">
              <i className="fa fa-key" /> Finn nøkkelen din i <b>Brukerinnstillinger → API-nøkkel</b>
            </Text>
            <Group justify="flex-end" mt="xs">
              <Button
                variant="secondary"
                size="xs"
                onClick={openUserSettings}
                leftSection={<i className="fa fa-user-circle fa-fw" />}
              >
                Gå til brukerinnstillinger
              </Button>
            </Group>
          </Stack>
        </Alert>

        {/* Switch för tasks */}
        <Group gap="md" align="flex-start">
          <Switch
            checked={includeTasks}
            onChange={(event) => setIncludeTasks(event.currentTarget.checked)}
            size="md"
            color="blue"
            label={
              <Text size="sm" fw={500}>
                Inkluder underoppgaver (tasks)
              </Text>
            }
          />
          <Text size="xs" c="dimmed">
            ✅ <b>AV</b> = kun hovedoppgaver (issues)
            <br />✅ <b>PÅ</b> = hovedoppgaver + underoppgaver (tasks) - <i>Flere data</i>
          </Text>
        </Group>

        <Row label="🤖 DIN KOMPLETTE API URL" value={url || "—"} block />

        <Divider />

        <Alert variant="light" color="gray" icon={<i className="fa fa-lightbulb" />}>
          <Text size="sm">
            <b>FERDIG!</b> Lim inn URLen i Power BI "Web"-kilde eller andre verktøy.
          </Text>
        </Alert>

        <Divider />
        <PowerBICollapsible
          url={`${window.location.origin}/api/trpc/project/${projectId}/issues/external?issueboardId=${issueboardId}`}
          defaultOpened={false}
        />
      </Stack>
    </Modal>
  );
};

const IssueApiButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const projectId = useAppSelector(selectCurrentProjectId);
  const issueboardId = useAppSelector(selectIssueBoardId);

  const disabled = !projectId || !issueboardId;

  return (
    <>
      <Menu shadow="md" width={200}>
        <Menu.Target>
          <Tooltip
            label={disabled ? "Velg prosjekt og board først" : "Vis API-endepunkt for dette boardet"}
            withinPortal
          >
            <ActionIcon variant="subtle" size="xs" disabled={disabled}>
              <div>
                <IconDotsVertical size={22} />
              </div>
            </ActionIcon>
          </Tooltip>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Item onClick={() => setOpen(true)} leftSection={<i className="fa fa-link fa-fw" />} disabled={disabled}>
            API
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <IssueApiModal opened={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default IssueApiButton;
